import * as React from "react";
import { JSONUIProvider, Renderer, type StateStore } from "@json-render/react";
import { internalUiComponents } from "./components";
import type { InternalUiSpec } from "./catalog";

/**
 * Client-side renderer for internal-UI json-render specs.
 *
 * This is the browser entry point (`@erp/json-renderer/react`). It pulls in
 * `@erp/ui`, so it must never be imported from the package's server-safe main
 * entry — keep backend imports on `@erp/json-renderer` only.
 */

export interface InternalUiRendererProps {
  /** The spec to render. `null` renders nothing (e.g. while loading). */
  spec: InternalUiSpec | null;
  /** Initial state model that `{ $state }` / `{ $bindState }` expressions read from. */
  state?: Record<string, unknown>;
  /**
   * Controlled state store — the single source of truth for `{ $state }`
   * bindings. Provide one (e.g. via `createStateStore`) when action handlers
   * need to mutate the rendered view: handlers close over the same store and
   * call `store.set`/`store.update`, and the renderer re-renders. When a `store`
   * is given, `state`/`onStateChange` are ignored (controlled mode).
   */
  store?: StateStore;
  /** Action handlers, keyed by the event name a component emits (e.g. `press`). */
  handlers?: Record<
    string,
    (params: Record<string, unknown>) => Promise<unknown> | unknown
  >;
  /** Called whenever bound state changes (e.g. a two-way-bound Input). */
  onStateChange?: (changes: Array<{ path: string; value: unknown }>) => void;
  /** Whether the spec is still streaming/loading. */
  loading?: boolean;
}

/**
 * Render an internal-UI spec as live React using the `@erp/ui` design system.
 *
 * @example
 * ```tsx
 * <InternalUiRenderer spec={spec} state={{ form: { name: "" } }} />
 * ```
 */
export function InternalUiRenderer({
  spec,
  state,
  store,
  handlers,
  onStateChange,
  loading,
}: InternalUiRendererProps): React.JSX.Element {
  return (
    <JSONUIProvider
      registry={internalUiComponents}
      store={store}
      initialState={state}
      handlers={handlers}
      onStateChange={onStateChange}
    >
      <Renderer spec={spec} registry={internalUiComponents} loading={loading} />
    </JSONUIProvider>
  );
}

export { internalUiComponents } from "./components";
// Controlled-store primitives, re-exported so consumers can build an external
// store for interactive (state-mutating) action handlers without importing
// `@json-render/*` directly.
export { createStateStore } from "@json-render/react";
export type { StateStore } from "@json-render/react";
export {
  buildInternalUiCatalog,
  internalUiCatalog,
  internalUiSchema,
  validateInternalUiSpec,
} from "./catalog";
export type {
  InternalUiActionDefs,
  InternalUiSpec,
  InternalUiValidationResult,
} from "./catalog";
export { internalUiSampleSpec, internalUiSampleState } from "./sampleSpec";
export { internalUiDemoSpec, internalUiDemoState } from "./demoSpec";
